<template>
  <collapsible-section class="_pt0 _pb0 -stretch-actions">
    <template #headerText>
      <p class="color-leaf">Member Coverage</p>
    </template>
    <template #headerActions>
      <dropdown-link title="Edit Coverage" class="_mls _mt0 btn --sm white" v-if="memberManagementAllowed && !hideOtherButtons">
        <li>
          <router-link :to="{name: 'coverage-add', params: { memberCk: memberCk }}">Reinstate</router-link>
        </li>
        <li v-if="!memberDetails.member.memberVoided">
          <a @click="confirmVoidMember" href="javascript:void(0)">Void Member</a>
        </li>
      </dropdown-link>
    </template>
    <template #headerActionsRight>
      <slot name="collapser"></slot>
    </template>
    <template #collapsibleSection>
      <div v-if="showConfirmVoidMember" container>
        <div class="announcements _mbxs">
          <div class="announcement -warning">
            <p>This should not be used to term this member. This will eliminate the member's benefits.</p>
            <div class="anncoucement-button _flex">
              <app-placeholder-input
                v-if="isInternalUser"
                class="_c-text-primary _b1"
                v-model="$v.fileBoundId.$model"
                valueKey="fileBoundId"
                label="Filebound ID"
                :required="$isInternalUser"
                :errorMessage="fileBoundIdError"
                :invalid="$v.fileBoundId.$invalid"
                />
              <button class="_mlxs btn --sm" @click="showConfirmVoidMember = false">No</button>
              <async-button
                class="_mlxs btn --sm white"
                @clicked="voidMember"
                :disabled='$v.$invalid'
                :executing="voidMemberSubmitting"
              >
                <template #buttonContent>
                  <span>
                    Void
                  </span>
                </template>
              </async-button>
            </div>
          </div>
        </div>
      </div>
      <async-loader v-if="memberDetailsLoading"/>
      <div v-else container>
        <div row class="_pbs">
          <app-advanced-table
            title="Group Results"
            class="_ptxs _pb0"
            :headers="computedCoverageHeaders"
            :items="computedCoverages"
            :clickable="memberManagementAllowed"
            @cellClicked="cellClicked"
            sortBy="orderInService"
            :rowsPerPage="3"
          >
            <template
            #items="{ item, visibleHeaders, getCellValue }"

            >
              <tr tabindex="0">
                <td v-for="(header, index) in visibleHeaders" :key="index">
                  <template v-if="header.value === 'action'">
                    <a @click="cellClicked(item)">Modify/Term</a>
                  </template>
                  <template v-else>
                    {{ getCellValue(item, header) }}
                  </template>
                </td>
              </tr>
            </template>
          </app-advanced-table>
        </div>
        <div grid="row wrap">
          <div
            column="s-6 m-3 l-3"
            v-if="['member-details'].indexOf($route.name) > -1 && memberDetails.member.benefitOption && memberDetails.member.benefitOption.trim()"
          >
            <labeled-data label="Benefit Option" :data="memberDetails.member.benefitOption"/>
          </div>
          <div
            column="s-6 m-3 l-3"
            v-if="['member-details'].indexOf($route.name) > -1 && memberDetails.member.divisionCode && memberDetails.member.divisionCode.trim()"
            >
            <labeled-data label="Division Code" :data="memberDetails.member.divisionCode"/>
          </div>
          <div column="s-6 m-3 l-3" v-if="['member-details'].indexOf($route.name) > -1 && memberDetails.member.location && memberDetails.member.location.trim()">
            <labeled-data label="Location Code" :data="memberDetails.member.location"/>
          </div>
          <div column="s-6 m-3 l-3" v-if="['member-details'].indexOf($route.name) > -1 && memberDetails.member.companyCode && memberDetails.member.companyCode.trim()">
            <labeled-data label="Company Code" :data="memberDetails.member.companyCode"/>
          </div>
          <div column="s-6 m-3 l-3" v-if="['member-details'].indexOf($route.name) > -1 && memberDetails.member.clientGroupNbr && memberDetails.member.clientGroupNbr.trim()">
            <labeled-data label="Client Group #:" :data="memberDetails.member.clientGroupNbr"/>
          </div>
          <div
            column="s-6 m-3 l-3"
            v-if="['member-details'].indexOf($route.name) > -1 && memberDetails.member.reportingCodes[0] && memberDetails.member.reportingCodes[0].trim()"
          >
            <labeled-data label="Reporting Code 1" :data="memberDetails.member.reportingCodes[0]"/>
          </div>
          <div
            column="s-6 m-3 l-3"
            v-if="['member-details'].indexOf($route.name) > -1 && memberDetails.member.reportingCodes[1] && memberDetails.member.reportingCodes[1].trim()"
          >
            <labeled-data label="Reporting Code 2" :data="memberDetails.member.reportingCodes[1]"/>
          </div>
          <div
            column="s-6 m-3 l-3"
            v-if="['member-details'].indexOf($route.name) > -1 && memberDetails.member.reportingCodes[2] && memberDetails.member.reportingCodes[2].trim()"
          >
            <labeled-data label="Reporting Code 3" :data="memberDetails.member.reportingCodes[2]"/>
          </div>
          <div
            column="s-6 m-3 l-3"
            v-if="['member-details'].indexOf($route.name) > -1 && memberDetails.member.reportingCodes[3] && memberDetails.member.reportingCodes[3].trim()"
          >
            <labeled-data label="Reporting Code 4" :data="memberDetails.member.reportingCodes[3]"/>
          </div>
          <div
            column="s-6 m-3 l-3"
            v-if="['member-details'].indexOf($route.name) > -1 && memberDetails.member.reportingCodes[4] && memberDetails.member.reportingCodes[4].trim()"
          >
            <labeled-data label="Reporting Code 5" :data="memberDetails.member.reportingCodes[4]"/>
          </div>
        </div>
      </div>
    </template>
  </collapsible-section>
</template>

<script>
  import AsyncButton from '@molecules/AsyncButton'
  import CollapsibleSection from '@sections/CollapsibleSection'
  import LabeledData from '@molecules/LabeledData'
  import AppAdvancedTable from '@organisms/AdvancedTable'
  import DropdownLink from '@atoms/DropdownLink'
  import AppPlaceholderInput from '@atoms/PlaceholderInput'

  import MemberEditApi from '@api/MemberEditApi'

  import formattable from '@mixins/formattable'
  import CoverageHelper from '@mixins/CoverageHelper'
  import ValueInputBindings from '@mixins/ValueInputBindings'
  import validationMessages from '@mixins/validationMessagesInternalValue'
  import memberManagement from '@mixins/memberManagement'
  import pushable from '@mixins/pushable'

  import { requiredIf, maxLength } from 'vuelidate/lib/validators'
  import { fileBoundIdValidation } from '@/utils'

  export default {
    mixins: [
      memberManagement,
      formattable,
      ValueInputBindings,
      validationMessages,
      CoverageHelper,
      pushable
    ],
    components: {
      AsyncButton,
      CollapsibleSection,
      LabeledData,
      AppAdvancedTable,
      DropdownLink,
      AppPlaceholderInput
    },
    props: {
      value: {
        type: Object,
        default: function() {
          return {
            clientHealthId: '',
            fileBoundId: ''
          }
        }
      }
    },
    computed: {
      fileBoundIdError() {
        if (!this.$v.fileBoundId.required) {
          return 'required'
        } else if (!this.$v.fileBoundId.maxLength) {
          return 'character limit exceeded'
        } else if (!this.$v.fileBoundId.alphaNum) {
          return 'alphanumeric characters only'
        }
        return null
      },
      isInternalUser() {
        return this.$store.getters.isInternalUser
      },
      pageType() {
        return this.$route.name == 'member-details' ? 'read-only' : 'editable'
      },
      memberCk(){
        return this.$store.getters['hiddenRouteData/getHiddenRouteData'].memberCk
      }
    },
    data() {
      return {
        hideOtherButtons: false,
        showConfirmVoidMember: false,
        voidMemberSubmitting: false,
        coverageHeaders: [
          { text: 'Class', align: 'left', value: 'classDescription' },
          {
            text: 'Plan',
            value: item => `${item.planId} - ${item.planDescription}`
          },
          {
            text: 'Subgroup',
            value: item => `${item.subGroup} - ${item.subGroupId}`
          },
          {
            text: 'Effective Date',
            value: item => this.formatDate(item.effectiveDate)
          },
          { text: 'Term Date', value: item => this.formatDate(item.termDate) }
        ],
        fileBoundId: ''
      }
    },
    methods: {
      cellClicked(e) {
        if (!this.memberManagementAllowed) {
          return
        }

        this.$router.push({
          name: 'coverage-edit',
          params: {
            memberCk: this.memberCk,
            subGroupCk: e.subGroupCk,
            classId: e.classId,
            planId: e.planId,
          },
          query: {
            productId: e.productId,
            effectiveDate: e.effectiveDate,
            termDate: e.termDate
          }
        })
      },
      confirmVoidMember() {
        this.showConfirmVoidMember = true
      },
      voidMember() {
        this.voidMemberSubmitting = true

        const body = {
          effectiveDate: this.toIso8601Date(this.computedFirstCoverage.effectiveDate),
          terminationDate: this.toIso8601Date(this.computedFirstCoverage.termDate),
          groupCk: this.computedFirstCoverage.groupCk,
          subGroupCk: this.computedFirstCoverage.subGroupCk,
          planId: this.computedFirstCoverage.planId,
          classId: this.computedFirstCoverage.classId,
          subscriberCk: `${this.memberDetails.member.subscriberCk}`,
          memberCk: `${this.memberDetails.member.memberCk}`,
          memberType: this.memberDetails.member.memberType,
          fileBoundId: this.fileBoundId,
          voidMember: true
        }

        new MemberEditApi({
          simulateExample: 'Void Member - Dependent.'
        })
          .voidMember({
            body: body
          })
          .then(resp => {
            this.showConfirmVoidMember = false
            this.$store.commit('SET_ANNOUNCEMENT', 'Member voided')
            this.pushOrGo({
              name: 'member-details',
              params: { memberCk: this.memberCk }
            })
          })
          .catch(err => {
            err.response.text().then(res => console.warn(res))
          })
          .finally(() => {
            this.voidMemberSubmitting = false
          })
      }
    },
    validations: {
      fileBoundId: {
        required: requiredIf(function() {
          return this.$isInternalUser
        }),
        maxLength: maxLength(15),
        fileBoundIdValidation
      }
    }
  }
</script>
